// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Floor plan view with positioned zones
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { FloorPlan, Table } from '@/lib/types';

interface FloorPlanViewProps {
  floorPlans: FloorPlan[];
  tables: Record<string, Table>;
  onTableClick: (id: string) => void;
}

const STATE_COLORS: Record<string, string> = {
  libre: 'bg-table-libre/30 border-table-libre text-table-libre',
  reservee: 'bg-table-reservee/30 border-table-reservee text-table-reservee',
  occupee: 'bg-table-occupee/30 border-table-occupee text-table-occupee',
  verrouillee: 'bg-table-verrouillee/30 border-table-verrouillee text-table-verrouillee',
};

export default function FloorPlanView({ floorPlans, tables, onTableClick }: FloorPlanViewProps) {
  const [activePlanIdx, setActivePlanIdx] = useState(0);

  if (floorPlans.length === 0) return null;

  const plan = floorPlans[activePlanIdx];
  if (!plan) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Plan tabs if multiple */}
      {floorPlans.length > 1 && (
        <div className="flex gap-2 px-4 py-2 overflow-x-auto shrink-0">
          {floorPlans.map((fp, idx) => (
            <button
              key={fp.id}
              type="button"
              onClick={() => setActivePlanIdx(idx)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activePlanIdx === idx
                  ? 'bg-accent text-white'
                  : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
              }`}
            >
              {fp.name}
            </button>
          ))}
        </div>
      )}

      {/* Floor plan canvas */}
      <div className="flex-1 relative bg-surface-card rounded-xl m-4 min-h-[300px] overflow-hidden">
        {(plan.zones ?? []).map((zone) => {
          const table = tables[zone.table_id];
          const state = table?.state ?? 'libre';
          const colorClass = STATE_COLORS[state] ?? STATE_COLORS.libre;

          return (
            <button
              key={zone.id}
              type="button"
              onClick={() => onTableClick(zone.table_id)}
              className={`absolute border-2 rounded-lg flex flex-col items-center justify-center text-xs font-medium transition-all hover:scale-105 ${colorClass}`}
              style={{
                left: `${zone.x}%`,
                top: `${zone.y}%`,
                width: `${zone.width}%`,
                height: `${zone.height}%`,
              }}
            >
              <span className="font-bold">{zone.name || table?.name || zone.table_id}</span>
              {table && <span className="opacity-60">{table.capacity}p</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
